/**
 * Defines an object that serves as a container for UIContainer child properties.  These properties may be fixed,
 * meaning that they should be set at construction time and not changed or dynamic meaning that they can change
 * to reflect and control the UIContainer's management of the child.  By extending "Stateful" the ChildProperties
 * class inherits get, set and watch methods that can be used to manage dynamic properties.
 *
 * @module MW/uiframework/ChildProperties
 * @copyright Copyright 2015-2020 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "MW/layout/LayoutChildProperty",
    "MW/layout/PerWindowStateful",
    "MW/layout/StatefulExtension",
    "MW/uiframework/uicontainer/WindowUtils",
    "mw-log/Log",
    "mw-utils/Utils"
], function (declare, lang, LayoutChildProperty, PerWindowStateful, StatefulExtension, WindowUtils, Log, Utils) {
    
    var isObject = function (obj) {
        return obj === Object(obj);
    }

    var hasProperties = function (obj) {
        // Unlike "Object.hasOwnProperty()", "for...in"
        //     1. Works with "Object.create(null)" maps
        //     2. Will search up the prototype chain
        if (isObject(obj)) {
            for (let prop in obj) {
                return true;
            }
        }
        return false;
    }

    /**
     * @class MW/uiframework/ChildProperties
     * @classdesc Defines an object to hold properties associated with UIContainer children.
     */
    var ChildProperties = declare([PerWindowStateful, StatefulExtension], {

        /**
         * @property {boolean} closable        Indicates whether a close affordance should be offered.
         * @property {String}  documentId      Uniquely identifies a particular document.  This is distinct from both the widget.id
         *                                     and any DOM id associated with document.  The document id must be unique among
         *                                     documents of the given type (see documentType) and must remain unchanged from
         *                                     session to session.  If the title property meets these criteria then a document
         *                                     id is not required. Applicable only to documents.
         * @property {String}  documentType    Identifies the type association.  Applicable only to documents.
         * @property {String}  childType       Identifies the type of child.  Used as a reference to identify derived classes.
         * @property {String}  panelId         Uniquely identifies a particular panel.  This is distinct from both the widget.id
         *                                     and any DOM id associated with the panel.  The panelId must remain the same from
         *                                     one session to the next.  Applicable only to panels.
         * @property {boolean}  isCollapsible  Controls whether the panel can be collapsed by the user.  This affects only the
         *                                     ability to collapse an individual panel within an AccordionContainer on the left
         *                                     or right not the ability to collapse the entire border region.  Applicable only to panels.
         * @property {boolean} isContextual    True if the panel is associated with a context.  Such panels are only shown
         *                                     when the context is active.  Applicable only to panels.
         * @property {boolean}  isDockable     Controls whether a document can be docked by the user.  By default docking is allowed.
         *                                     Setting this property to false does not prevent programmatic docking.
         * @property {boolean}  isMaximizable  Controls whether the child can be maximized by the user.  Currently this is only applicable
         *                                     to panels.  Documents are always considered maximizable.
         * @property {boolean}  isResizable    Controls whether the height of a panel within an AccordionContainer on the left
         *                                     or right can be adjusted by the user.  The width is always adjustable.
         *                                     Applicable only to panels.
         * @property {boolean} destroyOnClose  True if the child should automatically be destroyed when it is close.
         *                                     If this property isn't set it is treated as true by default.  Applicable only to documents.
         * @property {Array} permissibleRegions Indicates which border regions a panel can be moved to.  The array may contain
         *                                     any subset of "left, "right and "bottom".  If this property is not set
         *                                     then movement to any border will be permitted by default.  Applicable only to panels.
         * @property {number} preferredHeight  Specifies the height that should be allocated to the child provided there is
         *                                     flexibility to do so.
         *                                     If the value is >= 1 pixels are assumed.
         *                                     If the value is > 0 and < 1 it is interpreted as a fraction of the container height.
         *                                     If the value is <= 0 the height is obtained from the computed style of the child.
         *                                     Finally if this property is not set a default height will be allocated (typically 1/nth
         *                                     of the immediate parent height, where n is the number of panels the parent has).
         *                                     Applicable only to panels.
         * @property {number} preferredWidth   Specifies the width that should be allocated to the child provided there is
         *                                     flexibility to do so.
         *                                     If the value is >= 1 pixels are assumed.
         *                                     If the value is > 0 and < 1 it is interpreted as a fraction of the container width.
         *                                     If the value is <= 0 the width is obtained from the computed style of the child.
         *                                     Finally if this property is not set a default width will be allocated.
         *                                     Applicable only to panels.
         * @property {Object} qabControlToActionMap  Maps quick access toolbar control tags to corresponding action ids
         * @instance
         */
        Fixed: undefined,  // Trick to make the JSDoc generator list the above under "Fixed"
        /**
         * @property {Array}    actions        This array contains action objects, action id strings or nested arrays containing
         *                                     the same. When action id strings are provided they are assumed to refer to actions
         *                                     with the UIContainer's action service.  Action objects must include text, enabled
         *                                     and callback properties.  Access to the actions will be offered via an actions
         *                                     drop down and a title bar (for panels) or tab (for documents) context menu.
         * @property {Array}    activeContexts The strings within the array identify "contexts" which drive the inclusion of
         *                                     associated tab groups on the toolstrip and controls on the quick access toolbar.
         *                                     Applicable only to documents.
         * @property {function} canClose       This function will be called in response to user initiated close.  It is expected to
         *                                     return either a boolean or a Promise.  The promise must be resolved to true for close
         *                                     to proceed.  Otherwise it should be rejected to veto the close.
         *                                     Applicable only to documents.
         * @property {function} cleanup        This function will be called upon child close.  The intent is to provide on opportunity
         *                                     for potentially asynchronous cleanup which might require client-server communication.
         *                                     The cleanup function may return a promise, in which case, if the child
         *                                     is being closed as part of container close, the container itself and any associated
         *                                     communication services will remain open until the promise is resolved.  This behavior
         *                                     can only be guaranteed when the container is hosted in a CEF window rather than a browser.
         *                                     The returned promise should never be rejected and should always eventually be resolved.
         * @property {String}   description    Describes the the child.  The description may be displayed in a tooltip.
         * @property {number}   index          Reflects/controls the position of the child relative to others that share its parent
         *                                     (TabContainer or AccordionContainer).
         * @property {boolean}  isCollapsed    Reflects/controls whether the panel is collapsed.  Applicable only to panels.
         * @property {boolean}  isDocked       Reflects/controls whether a document is docked.  Applicable only to documents.
         * @property {boolean}  isMaximized    Reflects/controls whether the document is maximized.  Applicable only to documents.
         * @property {boolean}  isOpen         Reflects/controls whether the child is open.  A child is considered open when it has
         *                                     a presence in the current layout, be it the child window itself or a control, such as a tab,
         *                                     that can be used to access the window.
         * @property {boolean}  isPhantom      If true a document child will not actually appear in the container when added.
         *                                     However, if the isPhantom property value is subsequently changed to false the document
         *                                     will appear.
         * @property {boolean}  isShowing      Reflects/controls whether the child is currently visible, i.e. not hidden
         *                                     behind some other child in a tab container, collapsed in an accordion container
         *                                     or within a collapsed border panel.
         * @property {boolean}  isSelected     Reflects/controls whether the child is selected.  Selection is related to but slightly
         *                                     different from focus.  When a child receives focus it also becomes selected.
         *                                     However, when a child loses focus it doesn’t necessarily become deselected.
         *                                     It only becomes deselected if focus has gone to another child. For example,
         *                                     a document will lose focus but not selection if the user clicks within a
         *                                     toolstrip text field.
         * @property {String} region           Reflects/controls the placement of the child.  The permitted values are
         *                                     "left", "right", and "bottom".  Applicable only to panels.
         * @property {String} shortTitle       Variant of the title to be displayed when brevity is important.  If no shortTitle is
         *                                     specified the title will be used.
         * @property {String} tile             Reflects/controls the placement of a document within the document grid.
         * @property {String} title            Displayed title.  May be localized.
         * @property {{x: number, y: number, w: number, h: number}} windowBounds
         *                                     Reflects/controls the position and size of the window containing the child.  This
         *                                     is only applicable to undocked documents.  While a document is docked the value of
         *                                     the windowBounds property does not reflect its position and size within its container
         *                                     nor does it reflect the position and size of the containing window.
         *                                     For each of the constituent numeric values, if the value is an integer pixels are assumed.
         *                                     If 0 < value < 1 the value is assumed to be a fraction of the screen size.
         * @property {String} origin           Reflects/controls whether windowBounds and bounds are reported from a bottom-left or top-left
         *                                     point of reference.  By default origin is set to top-left.  Use ChildProperties.BOTTOM_LEFT_ORIGIN
         *                                     and ChildProperties.TOP_LEFT_ORIGIN constant values to set origin property.
         * @instance
         */
        Dynamic: undefined,  // Trick to make the JSDoc generator list the above under "Dynamic"
        
        /**
         * Creates a ChildProperties instance
         *
         * @param initialProperties  an object containing properties that will be mixed into the constructed object
         * @instance
         */
        constructor: function (initialProperties) {
            declare.safeMixin(this, initialProperties);

            delete this.Fixed;   // Not a real property just a JSDoc trick
            delete this.Dynamic; // Not a real property just a JSDoc trick

            this.__lockedProperties = [];

            // Update the WINDOW_BOUNDS when they are changed
                // Set '__windowBoundsListenerWindow' property BEFORE calling '_updateWindowBoundsListener()' method,
                // since method does not require it in initial call, and we then only need to execute the conditional once.
            this.__windowBoundsListenerWindow = this[ChildProperties.IS_DOCKED] ? window : (this.__undockedWindow || window);
            this._updateWindowBoundsListener(this.__windowBoundsListenerWindow);
            
            // Update the WINDOW_BOUNDS listener if the origin is changed
            this.watch(ChildProperties.ORIGIN, function (name, oldValue, newValue) {
                this.__locallySetOriginValue = true;
                this.__inheritedOriginValue = false;
                this._handleOriginChange(name, oldValue, newValue);
            }.bind(this));
            
            this._watchAlias('origin_inherit', function (propName, newValue) {
                if (this.__locallySetOriginValue) {
                    return true;
                }
                this.__inheritedOriginValue = true;
                this._handleOriginChange(name, null, newValue);
                this[propName] = newValue;
                return true; // preventSet
            }.bind(this));

            // Lock/unlock the ability to set the WINDOW_BOUNDS based on whether child is docked
            this.watch(ChildProperties.IS_DOCKED, (name, oldValue, newValue) => {
                if (newValue) {
                    this._updateWindowBoundsListener(window);
                    // Set '__windowBoundsListenerWindow' property AFTER calling '_updateWindowBoundsListener()' method,
                    // since method uses previous property value as a reference.
                    this.__windowBoundsListenerWindow = window;
                    this._lockProperty(ChildProperties.WINDOW_BOUNDS);
                } else {
                    let undockedWindow = this.__undockedWindow || window;
                    this._updateWindowBoundsListener(undockedWindow);
                    // Set '__windowBoundsListenerWindow' property AFTER calling '_updateWindowBoundsListener()' method,
                    // since method uses previous property value as a reference.
                    this.__windowBoundsListenerWindow = undockedWindow;
                    this._unlockProperty(ChildProperties.WINDOW_BOUNDS);
                }
            });
        },
        
        _handleOriginChange: function (name, oldValue, newValue) {
            this.__windowBoundsListenerWindow = this[ChildProperties.IS_DOCKED] ? window : (this.__undockedWindow || window);
            this._updateWindowBoundsListener(this.__windowBoundsListenerWindow);
            this[ChildProperties.WINDOW_BOUNDS] = WindowUtils.getWindowBounds(this.__windowBoundsListenerWindow, newValue);
        },
        
        _handleWindowBoundsChange: function (bounds) {
            this.__respondingToBoundsChange = true;  // Block response to property change
            this._changeAttrValue(ChildProperties.WINDOW_BOUNDS, bounds);
            this.__respondingToBoundsChange = false;
        },
        
        _addWindowBoundsListener: function (newWindow) {
            if (!this.__undockedWindow || (window !== this.__undockedWindow)) {
                this.__windowBoundsListenerId = WindowUtils.addWindowBoundsListener(newWindow, this._handleWindowBoundsChange.bind(this), this.origin);
            }
        },
        
        _removeWindowBoundsListener: function (newWindow) {
            if (this.__windowBoundsListenerId) {
                WindowUtils.removeWindowBoundsListener(this.__windowBoundsListenerId);
                delete this.__windowBoundsListenerId;  // Cleanup in case of error
            }
        },
        
        _updateWindowBoundsListener: function (newWindow) {
            this._removeWindowBoundsListener(newWindow);
            this._addWindowBoundsListener(newWindow);
        },

        set: function (name, newValue) {
            // If an object is used, iterate through object
            if(typeof name === "object"){
                for(var x in name){
                    if(name.hasOwnProperty(x) && x !="_watchCallbacks"){
                        this.set(x, name[x]);
                    }
                }
                return;
            }
            
            if (                                                          // Ignore calls to 'set' where:
                (typeof name !== "string")                                || // 'name' is not a string
                (this._isPropertyLocked(name))                            || // Property is locked
                (this[this._getPropertyNameFromAlias(name)] === newValue)    // Value does not change
               ) {
                return;
            }

            // Substitute the true property name from an aliased name
            if (this._isAliasForPropertyName(name)) {
                if (this._handleAliasWatch(name, newValue)) {
                    return;
                }
                arguments[0] = this._getPropertyNameFromAlias(name);
            }

            this.inherited(arguments);
        },

        _lockProperty: function (name) {
            if (!this._isPropertyLocked(name)) {
                this.__lockedProperties.push(name);
            }
        },

        _unlockProperty: function (name) {
            if (this._isPropertyLocked(name)) {
                this.__lockedProperties.splice(this.__lockedProperties.indexOf(name), 1);
            }
        },

        _isPropertyLocked: function (name) {
            return (this.__lockedProperties.indexOf(name) > -1);
        },

        _isAliasForPropertyName: function (name) {
            return propertyAliasMap.hasOwnProperty(name);
        },

        _getPropertyNameFromAlias: function (alias) {
            if (this._isAliasForPropertyName(alias)) {
                return propertyAliasMap[alias];
            }
            return alias;
        },
        
        _watchAlias: function (alias, callback) {
            if (!this.__aliasWatches) {
                this.__aliasWatches = Object.create(null);
            }
            var uuid = Utils.generateUuid();
            
            if (!(alias in this.__aliasWatches)) {
                this.__aliasWatches[alias] = Object.create(null);
            }
            
            this.__aliasWatches[alias][uuid] = callback;
            
            return {
                unwatch: () => {
                    delete this.__aliasWatches[alias][uuid];
                    if (!hasProperties(this.__aliasWatches[alias])) {
                        delete this.__aliasWatches[alias];
                    }
                }
            };
        },
        
        _handleAliasWatch: function (alias, newValue) {
            var preventSet;
            if (alias in this.__aliasWatches) {
                for (let uuid in this.__aliasWatches[alias]) {
                    preventSet = this.__aliasWatches[alias][uuid](this._getPropertyNameFromAlias(alias), newValue);
                }
            }
            return preventSet;
        }
    });

    // Constant name definitions for dynamic properties
    ChildProperties.ACTIONS = LayoutChildProperty.ACTIONS;
    ChildProperties.ACTIVE_CONTEXTS = "activeContexts";
    ChildProperties.INDEX = LayoutChildProperty.INDEX;
    ChildProperties.DESCRIPTION = LayoutChildProperty.DESCRIPTION;
    ChildProperties.DESTROY_ON_CLOSE = LayoutChildProperty.DESTROY_ON_CLOSE;
    ChildProperties.IS_COLLAPSED = LayoutChildProperty.IS_COLLAPSED;
    ChildProperties.IS_DOCKED = "isDocked";
    ChildProperties.IS_MAXIMIZED = LayoutChildProperty.IS_MAXIMIZED;
    ChildProperties.IS_OPEN = LayoutChildProperty.IS_OPEN;
    ChildProperties.IS_PHANTOM = "isPhantom";
    ChildProperties.IS_SHOWING = LayoutChildProperty.IS_SHOWING;
    ChildProperties.IS_SELECTED = LayoutChildProperty.IS_SELECTED;
    ChildProperties.REGION = LayoutChildProperty.REGION;
    ChildProperties.SHORT_TITLE = LayoutChildProperty.SHORT_TITLE;
    ChildProperties.TILE = LayoutChildProperty.TILE;
    ChildProperties.TITLE = LayoutChildProperty.TITLE;
    ChildProperties.WINDOW_BOUNDS = "windowBounds";
    ChildProperties.ORIGIN = "origin";

    // Constant name definitions for some fixed properties
    ChildProperties.IS_COLLAPSIBLE = LayoutChildProperty.IS_COLLAPSIBLE;
    ChildProperties.IS_MAXIMIZABLE = LayoutChildProperty.IS_MAXIMIZABLE;
    ChildProperties.IS_RESIZABLE = LayoutChildProperty.IS_RESIZABLE;
    ChildProperties.PERMISSIBLE_REGIONS = LayoutChildProperty.PERMISSIBLE_REGIONS;
    ChildProperties.PREFERRED_WIDTH = LayoutChildProperty.PREFERRED_WIDTH;
    ChildProperties.PREFERRED_HEIGHT = LayoutChildProperty.PREFERRED_HEIGHT;
    
    // Constant name definitions for enum properties
    ChildProperties.BOTTOM_LEFT_ORIGIN = WindowUtils.BOTTOM_LEFT_ORIGIN;
    ChildProperties.TOP_LEFT_ORIGIN = WindowUtils.TOP_LEFT_ORIGIN;

    // We do not want to advertise these aliases, so we are not adding a constant name
    var propertyAliasMap = {
        "windowBounds_force": ChildProperties.WINDOW_BOUNDS,
        "origin_inherit": ChildProperties.ORIGIN
    };

    return ChildProperties;
});